import { tiltify } from "@playlive/tiltify-lib";
import { deepSignal } from "deepsignal";
import { gunzipSync, gzipSync, strFromU8, strToU8 } from "fflate";
import { decode, encode } from "js-base64";
import { useRef } from "preact/compat";
import { useCallback, useEffect, useState } from "preact/hooks";
import PropTypes from "prop-types";
import useTiltifyConfig from "@/hooks/useTiltifyConfig.js";
import TTSPollOption from "./tts-poll-option.jsx";

TTSPollSetup.propTypes = {
  pollID: PropTypes.string.isRequired,
  pollMappingChanged: PropTypes.func.isRequired,
};

const pollConfig = deepSignal({
  mapping: {},
});

const encodeMapping = (mapping) => {
  return encode(strFromU8(gzipSync(strToU8(JSON.stringify(mapping), true)), true));
};

const decodeMapping = (mapping) => {
  try {
    const decodedRaw = strFromU8(gunzipSync(strToU8(decode(mapping), true)), true);
    return JSON.parse(decodedRaw);
  } catch (_e) {}
  return null;
};

export default function TTSPollSetup({ pollID, pollMappingChanged }) {
  let pollMappingBlock = null;
  const tiltifyConfig = useTiltifyConfig();
  const [pollData, setPollData] = useState({ loading: false, options: [] });
  const pollIDRef = useRef(pollID);
  const saveMapping = useCallback(
    (mapping) => {
      if (Object.keys(mapping).length !== 0) {
        const encoded = encodeMapping(mapping);
        pollMappingChanged(encoded);
      }
    },
    [pollMappingChanged],
  );

  const fetchPollOptions = useCallback(
    (pollID) => {
      const { tiltifyCampaignID, tiltifyUserID } = tiltifyConfig;
      if (
        !tiltifyCampaignID ||
        tiltifyCampaignID.length === 0 ||
        !tiltifyUserID ||
        tiltifyUserID.length === 0 ||
        !pollID ||
        pollID.length === 0
      ) {
        return new Promise((resolve) => resolve({ options: [] }));
      }
      tiltify.UseProxy();
      try {
        return tiltify.GetPoll(tiltifyCampaignID, pollID).then((value) => {
          if (value) {
            return value;
          }
          return { options: [] };
        });
      } catch (err) {
        throw new Error(err.message);
      }
    },
    [tiltifyConfig],
  );

  useEffect(() => {
    pollIDRef.current = pollID;

    setPollData({
      loading: true,
      options: [],
    });

    fetchPollOptions(pollID).then((data) => {
      setPollData({
        loading: false,
        options: data.options,
      });
    });
  }, [fetchPollOptions, pollID]);

  const mapPoll = useCallback(
    ({ selectedVoice, id }) => {
      pollConfig.mapping[id] = selectedVoice.name;
      pollConfig.mapping = { ...pollConfig.mapping };
      saveMapping(pollConfig.mapping);
    },
    [saveMapping],
  );

  useEffect(() => {
    const encodedCompressedMapping = localStorage.getItem("pollMapping");
    if (!encodedCompressedMapping || encodedCompressedMapping.length === 0) {
      return;
    }

    const decodedMapping = decodeMapping(encodedCompressedMapping);

    if (decodedMapping) {
      pollConfig.mapping = decodedMapping;
    }
  }, []);

  if (!pollData || pollID.length === 0) {
    return null;
  }

  if (!pollData.loading) {
    pollMappingBlock = pollData.options.map((option, index) => (
      <>
        <TTSPollOption
          className={index < pollData.options.length - 1 ? "mt-3 mb-4" : "mt-3"}
          pollOption={option}
          key={option.id}
          onVoiceSelected={mapPoll}
          mapping={pollConfig.mapping}
        />
      </>
    ));
  } else {
    pollMappingBlock = (
      <div className="min-h-44 flex w-full justify-center items-center">
        <span className="loading loading-spinner loading-md" />
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="divider" />
      <div>
        <span>Poll Options:</span>
        <p className="mt-4 text-sm">Use the dropdown(s) below to map your poll option to a voice</p>
      </div>
      <div
        className={`collapse ${!pollData.loading && pollData.options.length === 0 ? "collapse-close" : "collapse-open"}`}
      >
        <div className="collapse-content py-0 px-[5px]">{pollMappingBlock}</div>
      </div>
    </div>
  );
}
